import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { useTranslation } from 'react-i18next'

import {
  Box,
  Flex,
  NativeSelectField,
  NativeSelectIndicator,
  NativeSelectRoot,
  Text,
} from '@enechain/ecloud-designsystem'

import { convertAreaLabel } from '~/models/area'
import type { WeatherForecastPointRow } from '~/pages/AdminWeatherForecastPoints/models/types'

type Props = {
  rows: WeatherForecastPointRow[]
  isViewer: boolean
  onSelectChange: (areaCode: AreaCode, locationName: string) => void
}

/**
 * エリア別 気象予測地点のカードグリッド。
 *
 * design `design/admin/weather-forecast-points.html` `.wp-grid` / `.wp-area-card` 準拠:
 * - grid: repeat(auto-fill, minmax(190px, 1fr)) / gap 12px
 * - カード = エリア名 + 全幅 select (選んだ時にエリアごとに保存するので未保存 badge は無い。AC-WFP-003)
 * - AC-WFP-002: 「未選択」を表す選択肢は提供しない (option 集合 = 候補リスト。
 *   design HTML 側にはまだ「選択」option が残るが、spec 側の廃止判断 (issue #5191)
 *   が優先する)
 * - AC-WFP-007: `row.locationName` は backend 解決済みの実効地点で、必ず候補
 *   リストに含まれる。ここで候補外を補正する分岐は持たない
 * - 閲覧者ロール: isViewer=true 時に select 無効化 (spec スコープ外・AC なし)
 */
export const WeatherForecastPointCards = ({
  rows,
  isViewer,
  onSelectChange,
}: Props): React.ReactElement => {
  const { t } = useTranslation()

  return (
    <Box
      gap={3}
      gridTemplateColumns="repeat(auto-fill, minmax(190px, 1fr))"
      display="grid"
    >
      {rows.map((row) => {
        const currentValue =
          row.pendingLocationName !== undefined
            ? row.pendingLocationName
            : row.locationName
        const areaLabel = convertAreaLabel(row.areaCode)

        return (
          <Flex
            key={row.areaCode}
            flexDirection="column"
            gap={2}
            padding={3}
            background="Surface.primary"
            borderWidth="1px"
            borderColor="Border.lowEmphasis"
            borderRadius="base"
          >
            <Flex
              alignItems="center"
              justifyContent="space-between"
              gap={2}
              minHeight="22px"
            >
              <Text textStyle="label" color="Text.highEmphasis">
                {areaLabel}
              </Text>
            </Flex>
            {/* design `.ecl-select--sm` は 40px / 14px = ecloud md 相当 (sm=36px/12px は design より一段小さい) */}
            <NativeSelectRoot disabled={isViewer} size="md">
              <NativeSelectField
                fontSize="md"
                aria-label={t(
                  'page:adminWeatherForecastPoints.selectAriaLabel',
                  { area: areaLabel },
                )}
                onChange={(e) => {
                  onSelectChange(row.areaCode, e.target.value)
                }}
                value={currentValue}
              >
                {row.candidates.map((candidate) => (
                  <option
                    key={candidate.locationName}
                    disabled={!candidate.isAvailable}
                    value={candidate.locationName}
                  >
                    {candidate.isAvailable
                      ? candidate.locationName
                      : `${candidate.locationName}${t(
                          'page:adminWeatherForecastPoints.select.unavailableSuffix',
                        )}`}
                  </option>
                ))}
              </NativeSelectField>
              <NativeSelectIndicator />
            </NativeSelectRoot>
          </Flex>
        )
      })}
    </Box>
  )
}
